import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, TrendingUp, BedDouble, Users2, LogIn, LogOut, DollarSign, Sparkles, UtensilsCrossed, AlertCircle } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const StatCard = ({ label, value, sub, icon: Icon, accent }) => (
    <Card className="p-5 bg-white border border-slate-200 rounded-md shadow-none" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="flex justify-between items-start">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
            <Icon className={`w-4 h-4 ${accent || "text-slate-400"}`} />
        </div>
        <div className="mt-3 text-3xl font-display font-bold tracking-tight text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
);

const StatusPill = ({ label, count, color }) => (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
        <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${color}`} />
            <span className="text-sm text-slate-700">{label}</span>
        </div>
        <span className="text-sm font-mono font-semibold text-slate-900">{count}</span>
    </div>
);

export default function Dashboard() {
    const { activeHotelId, hotels } = useAuth();
    const [data, setData] = useState(null);
    const [trend, setTrend] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!activeHotelId) return;
        setLoading(true);
        Promise.all([
            api.get(`/dashboard/summary?hotel_id=${activeHotelId}`),
            api.get(`/dashboard/revenue-trend?hotel_id=${activeHotelId}&days=7`),
        ]).then(([s, t]) => {
            setData(s.data);
            setTrend(t.data);
        }).finally(() => setLoading(false));
    }, [activeHotelId]);

    const active = hotels.find((h) => h.id === activeHotelId);

    if (loading) {
        return (
            <AppShell title="Dashboard">
                <div className="flex items-center justify-center h-96">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                </div>
            </AppShell>
        );
    }

    const currency = active?.currency || "INR";
    const fmtMoney = (n) => `${currency === "INR" ? "₹" : "$"} ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

    const chartData = trend?.labels?.map((l, i) => ({ date: l.slice(5), value: trend.values[i] })) || [];

    return (
        <AppShell title="Dashboard" subtitle={active ? `${active.name} · ${active.city}` : ""}>
            <div className="space-y-6">
                {/* KPI Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Occupancy" value={`${data?.occupancy_percent || 0}%`}
                        sub={`${data?.occupied || 0} of ${data?.total_rooms || 0} rooms`}
                        icon={TrendingUp} accent="text-emerald-600" />
                    <StatCard label="Revenue Today" value={fmtMoney(data?.revenue_today)}
                        sub="Collected today" icon={DollarSign} accent="text-[#C5B08A]" />
                    <StatCard label="Current Guests" value={data?.current_guests || 0}
                        sub="In-house" icon={Users2} accent="text-blue-600" />
                    <StatCard label="Pending Payments" value={fmtMoney(data?.pending_payments)}
                        sub="Across active" icon={AlertCircle} accent="text-orange-600" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Revenue Chart */}
                    <Card className="lg:col-span-2 p-6 bg-white border border-slate-200 rounded-md shadow-none">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Revenue Trend</div>
                                <div className="text-lg font-display font-semibold text-slate-900 mt-1">Last 7 days</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                                <div className="text-lg font-mono font-semibold text-slate-900">
                                    {fmtMoney((trend?.values || []).reduce((a, b) => a + b, 0))}
                                </div>
                            </div>
                        </div>
                        <div className="h-56" style={{ minHeight: 220 }}>
                            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                                    <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        contentStyle={{ background: "#0A1C3E", border: "none", borderRadius: 4, fontSize: 12, color: "#fff" }}
                                        labelStyle={{ color: "#C5B08A" }}
                                        formatter={(v) => fmtMoney(v)}
                                    />
                                    <Line type="monotone" dataKey="value" stroke="#0A1C3E" strokeWidth={2} dot={{ r: 3, fill: "#C5B08A" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    {/* Room Status */}
                    <Card className="p-6 bg-white border border-slate-200 rounded-md shadow-none">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-4">Room Status</div>
                        <StatusPill label="Available" count={data?.available || 0} color="bg-emerald-500" />
                        <StatusPill label="Occupied" count={data?.occupied || 0} color="bg-blue-500" />
                        <StatusPill label="Dirty" count={data?.dirty || 0} color="bg-red-500" />
                        <StatusPill label="Reserved" count={data?.reserved || 0} color="bg-indigo-500" />
                        <StatusPill label="Maintenance" count={data?.maintenance || 0} color="bg-orange-500" />
                    </Card>
                </div>

                {/* Today's arrivals / departures */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-5 border border-slate-200 rounded-md shadow-none flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-emerald-50 flex items-center justify-center">
                            <LogIn className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Check-ins Today</div>
                            <div className="text-2xl font-display font-bold text-slate-900">{data?.checkins_today || 0}</div>
                        </div>
                    </Card>
                    <Card className="p-5 border border-slate-200 rounded-md shadow-none flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-red-50 flex items-center justify-center">
                            <LogOut className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Check-outs Today</div>
                            <div className="text-2xl font-display font-bold text-slate-900">{data?.checkouts_today || 0}</div>
                        </div>
                    </Card>
                    <Card className="p-5 border border-slate-200 rounded-md shadow-none flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-amber-50 flex items-center justify-center">
                            <UtensilsCrossed className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Restaurant Today</div>
                            <div className="text-2xl font-display font-bold text-slate-900">{fmtMoney(data?.restaurant_sales_today)}</div>
                        </div>
                    </Card>
                </div>
            </div>
        </AppShell>
    );
}
